import axiosConfig from "../../../config/axiosConfig";
import {
  ACTIVITY_THRESHOLD_URL,
  ANALYTICS_URL,
  CUSTOMER_EMPLOYEE_ACTIVITY_SUMMARY_URL,
  CUSTOMER_FULL_QUOTES_URL,
  CUSTOMER_PN_URL,
  CUSTOMER_QUOTE_ACTIVITY_URL,
  CUSTOMER_QUOTE_GRAPHS_URL,
  CUSTOMER_QUOTE_HISTORY_TREND_URL,
  CUSTOMER_QUOTE_KPI_URL,
} from "../../../config/config";
import { createQueryParams, getStringFromArray } from "../../../utils/helper";

export const GetGraphsListService = (params) => {
  try {
    const { user, module, uuid } = params || {};

    const paramsList = {
      user,
      module,
      uuid,
    };

    const queryString = createQueryParams(paramsList);
    const url = `${ANALYTICS_URL}/module/graphs${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const GetAnalyticsFiltersService = () => {
  try {
    return axiosConfig.get(`${ANALYTICS_URL}/filters`);
  } catch (error) {
    throw error;
  }
};

export const GetAnalyticsGraphsService = (params) => {
  try {
    const { uuid, ...rest } = params || {};
    const paramsList = Object.fromEntries(
      Object.entries(rest).map(([key, value]) => {
        if (
          Array.isArray(value) &&
          value.every((item) => item.label && item.value)
        ) {
          return [key, getStringFromArray(value)];
        } else if (value && typeof value === "object" && "value" in value) {
          return [key, value.value];
        } else {
          return [key, value];
        }
      })
    );
    const queryString = createQueryParams(paramsList);

    const url = `${ANALYTICS_URL}/graphDetails/${uuid}${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const GetPartNumbersService = (params) => {
  try {
    const { tier } = params || {};

    const paramsList = {
      tier: tier?.value,
    };

    const queryString = createQueryParams(paramsList);
    const url = `${ANALYTICS_URL}/filters/partnumber${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const GetCustomerQuoteKPIService = (params) => {
  try {
    const { start_date, end_date } = params || {};

    const paramsList = {
      start_date,
      end_date,
    };

    const queryString = createQueryParams(paramsList);
    const url = `${CUSTOMER_QUOTE_KPI_URL}${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const GetCustomerQuoteActivityService = (params) => {
  try {
    const {
      page,
      per_page,
      start_date,
      end_date,
      customer,
      part_number,
      region,
      class_code,
      activity_flag,
      ordering,
    } = params || {};

    const paramsList = {
      page,
      per_page,
      start_date,
      end_date,
      customer,
      part_number,
      region,
      class_code,
      activity_flag,
      ordering,
    };

    const queryString = createQueryParams(paramsList);

    const url = `${CUSTOMER_QUOTE_ACTIVITY_URL}${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const GetCustomerDetailService = (params) => {
  try {
    const { cmp_auto_key, employee } = params || {};
    const paramsList = { CMP_AUTO_KEY: cmp_auto_key, EMPLOYEE: employee };
    const queryString = createQueryParams(paramsList);
    const url = `${CUSTOMER_EMPLOYEE_ACTIVITY_SUMMARY_URL}${queryString ? `?${queryString}` : ""}`;
    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const GetCustomerQuoteGraphsService = (params) => {
  try {
    const {
      start_date,
      end_date,
      customer,
      part_number,
      region,
      class_code,
      activity_flag,
    } = params || {};

    const paramsList = {
      start_date,
      end_date,
      customer,
      part_number,
      region,
      class_code,
      activity_flag,
    };

    const queryString = createQueryParams(paramsList);
    const url = `${CUSTOMER_QUOTE_GRAPHS_URL}${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const GetActivityThresholdService = (params) => {
  try {
    const paramsList = {};
    const queryString = createQueryParams(paramsList);
    const url = `${ACTIVITY_THRESHOLD_URL}${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const UpdateActivityThresholdService = (data) => {
  return axiosConfig.patch(ACTIVITY_THRESHOLD_URL, data);
};

export const GetQuoteHistoryTrendService = (params) => {
  try {
    const { cmp_auto_key } = params || {};

    const paramsList = {
      CMP_AUTO_KEY: cmp_auto_key,
    };

    const queryString = createQueryParams(paramsList);
    const url = `${CUSTOMER_QUOTE_HISTORY_TREND_URL}${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const GetCustomerPNService = (params) => {
  try {
    const {
      page,
      per_page,
      part_number,
      description,
      condition_code,
      quote_frequency,
      start_date,
      end_date,
      cmp_auto_key,
      ordering,
    } = params || {};

    const paramsList = {
      page,
      per_page,
      start_date,
      end_date,
      PART_NUMBER: getStringFromArray(part_number),
      DESCRIPTION: description,
      CONDITION_CODE: getStringFromArray(condition_code),
      quotes_count: quote_frequency,
      ordering,
      CMP_AUTO_KEY: cmp_auto_key,
    };

    const queryString = createQueryParams(paramsList);

    const url = `${CUSTOMER_PN_URL}${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};

export const GetFullQuoteService = (params) => {
  try {
    const {
      page,
      per_page,
      start_date,
      end_date,
      part_number,
      delivery_code,
      vq_status,
      ordering,
      cmp_auto_key,
    } = params || {};

    const paramsList = {
      page,
      per_page,
      start_date,
      end_date,
      DELIVERY_CODE: delivery_code,
      VQ_STATUS: getStringFromArray(vq_status),
      PART_NUMBER: getStringFromArray(part_number),
      ordering,
      CMP_AUTO_KEY: cmp_auto_key,
    };

    const queryString = createQueryParams(paramsList);

    const url = `${CUSTOMER_FULL_QUOTES_URL}${queryString ? `?${queryString}` : ""}`;

    return axiosConfig.get(url);
  } catch (error) {
    throw error;
  }
};
